// FormField
// ----------
// The standard layout wrapper for a single labelled form control. Use it for
// every Input/Select/Textarea so the label, control, helper text, and error
// message are vertically arranged the same way everywhere.
//
// FormField does NOT style the control itself — that's the control's own job.
// FormField only handles:
//   * the label (with optional required asterisk)
//   * the helper text (gray, below the control)
//   * the error message (red, below the control, takes precedence over helper)
//   * the gap and stacking
//
// Usage:
//   <FormField label="Email" htmlFor="email" required error={errors.email}>
//     <Input id="email" type="email" leftIcon={<Mail />} value={email} onChange={...} />
//   </FormField>
//
// Variant note: pass `variant="dark"` if you want the label color to suit a
// dark background. The control's own variant prop is independent.

import * as React from "react";
import { cn } from "@/lib/utils";

interface FormFieldProps {
  label?: React.ReactNode;
  htmlFor?: string;
  required?: boolean;
  helperText?: React.ReactNode;
  error?: string | null;
  variant?: "light" | "dark";
  className?: string;
  children: React.ReactNode;
}

const FormField = React.forwardRef<HTMLDivElement, FormFieldProps>(
  ({ label, htmlFor, required, helperText, error, variant = "light", className, children }, ref) => {
    const labelColor = variant === "dark" ? "text-white/70" : "text-gray-600";
    const helperColor = variant === "dark" ? "text-white/40" : "text-gray-400";

    return (
      <div ref={ref} className={cn("flex flex-col gap-1.5", className)}>
        {label && (
          <label htmlFor={htmlFor} className={cn("text-xs font-medium", labelColor)}>
            {label}
            {required && <span className="ml-0.5 text-red-500">*</span>}
          </label>
        )}
        {children}
        {error ? (
          <p className="text-xs text-red-500">{error}</p>
        ) : helperText ? (
          <p className={cn("text-xs", helperColor)}>{helperText}</p>
        ) : null}
      </div>
    );
  }
);
FormField.displayName = "FormField";

export { FormField };
